/**
 * Agent Identity API service.
 * Provides CRUD operations for agent identities.
 *
 * API Endpoints:
 * - GET /agentIdentities - List agent identities (paginated)
 * - GET /agentIdentities/:id - Get agent identity by ID
 * - PUT /agentIdentities - Create agent identity
 * - DELETE /agentIdentities/:id - Delete agent identity
 * - PATCH /agentIdentities/:id - Update agent identity (add/remove agent user)
 *
 * @module services/agentIdentityService
 */

import { httpClient } from './httpClient';
import type {
  AgentIdentityCreateRequest,
  AgentIdentityUpdateRequest,
  AgentIdentityResponse,
} from '@/types';

const ENDPOINT = '/agentIdentities';

/**
 * Agent Identity Service
 * Encapsulates all agent identity API operations
 */
export const agentIdentityService = {
  /**
   * List agent identities (paginated)
   * @param page - Page number (1-based)
   * @param pageSize - Number of items per page
   * @returns Promise<AgentIdentityResponse[]> - Array of agent identities
   */
  async list(page: number = 1, pageSize: number = 25): Promise<AgentIdentityResponse[]> {
    console.log(
      '[AgentIdentityService] Fetching agent identities, page:',
      page,
      'pageSize:',
      pageSize
    );
    try {
      const response = await httpClient.get<AgentIdentityResponse[]>(ENDPOINT, {
        params: { page, pageSize },
      });
      console.log('[AgentIdentityService] Fetched', response.data.length, 'agent identities');
      return response.data;
    } catch (error) {
      console.error('[AgentIdentityService] Failed to fetch agent identities:', error);
      throw error;
    }
  },

  /**
   * Get an agent identity by ID
   * @param id - The ID of the agent identity
   * @returns Promise<AgentIdentityResponse> - The agent identity
   */
  async getById(id: string): Promise<AgentIdentityResponse> {
    console.log('[AgentIdentityService] Fetching agent identity:', id);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.get<AgentIdentityResponse>(`${ENDPOINT}/${encodedId}`);
      console.log('[AgentIdentityService] Fetched agent identity:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[AgentIdentityService] Failed to fetch agent identity:', id, error);
      throw error;
    }
  },

  /**
   * Create a new agent identity
   * @param request - The agent identity request payload (includes tenantId)
   * @returns Promise<AgentIdentityResponse> - The created agent identity
   */
  async create(request: AgentIdentityCreateRequest): Promise<AgentIdentityResponse> {
    console.log('[AgentIdentityService] Creating agent identity:', request.displayName);
    try {
      const response = await httpClient.put<AgentIdentityResponse>(ENDPOINT, request);
      console.log('[AgentIdentityService] Created agent identity:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[AgentIdentityService] Failed to create agent identity:', error);
      throw error;
    }
  },

  /**
   * Delete an agent identity by ID
   * @param id - The ID of the agent identity to delete
   * @returns Promise<void>
   */
  async delete(id: string): Promise<void> {
    console.log('[AgentIdentityService] Deleting agent identity:', id);
    const encodedId = encodeURIComponent(id);
    try {
      await httpClient.delete(`${ENDPOINT}/${encodedId}`);
      console.log('[AgentIdentityService] Deleted agent identity:', id);
    } catch (error) {
      console.error('[AgentIdentityService] Failed to delete agent identity:', id, error);
      throw error;
    }
  },

  /**
   * Update an agent identity (add/remove agent user)
   * @param id - The ID of the agent identity to update
   * @param request - The update request payload
   * @returns Promise<AgentIdentityResponse> - The updated agent identity
   */
  async update(id: string, request: AgentIdentityUpdateRequest): Promise<AgentIdentityResponse> {
    console.log('[AgentIdentityService] Updating agent identity:', id);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.patch<AgentIdentityResponse>(
        `${ENDPOINT}/${encodedId}`,
        request
      );
      console.log('[AgentIdentityService] Updated agent identity:', id);
      return response.data;
    } catch (error) {
      console.error('[AgentIdentityService] Failed to update agent identity:', id, error);
      throw error;
    }
  },
};
